import { FilterOutlined, InfoCircleOutlined } from '@ant-design/icons'
import {
  Button,
  DatePicker,
  Flex,
  Select,
  Space,
  Tooltip,
  Typography,
} from 'antd'
import { Dayjs } from 'dayjs'
import React, { useState } from 'react'

import s from '@/components/feature/analytics/creator-reports.module.css'
import { EarningType, earningTypeOptions } from '@/constants/earning-type'
import { periodOptions, ReportPeriod } from '@/constants/report-period'
import useFormModal from '@/hooks/use-form-modal'
import { selectFeatureTimezone } from '@/redux/global-selector'
import { useAppSelector } from '@/redux/utils'
import { dayjs } from '@/utils/dayjs'

import { CreatorsFilterFields } from '../components/creators-filter-fields'
import { EmployeesFilterFields } from '../components/employees-filter-fields'

import useRangePickerOptions from './use-range-picker-options'

const { RangePicker } = DatePicker
const { Text } = Typography

const periodTooltipTitle = {
  [ReportPeriod.Week]:
    'Track weekly activity by setting the start and end dates within a range of 14 to 365 days',
  [ReportPeriod.Day]:
    'Track daily activity by setting the start and end dates within a 31-day window',
}

function autoCorrectPeriod(period: ReportPeriod, dayDiff: number) {
  if (period === ReportPeriod.Week && dayDiff < 14) {
    return ReportPeriod.Day
  }
  if (period === ReportPeriod.Day && dayDiff > 31) {
    return ReportPeriod.Week
  }
  return period
}

export interface ReportSearchValues {
  period: ReportPeriod
  earningType?: EarningType
  creatorIds?: number[]
  groupIds?: number[]
  employeeIds?: number[]
  startAt: Date
  endAt: Date
}

export interface ReportsPageProps {
  searchValues: ReportSearchValues
}

export interface UseReportSearchParams {
  enableCreatorsFilter?: boolean
  enableEmployeesFilter?: boolean
  enableEarningTypeSelect?: boolean
}

const monthAgo = dayjs().add(-1, 'month')
const yesterday = dayjs().add(-1, 'day')
const defaultDiff = yesterday.diff(monthAgo, 'd')

export function useReportSearch({
  enableCreatorsFilter,
  enableEmployeesFilter,
  enableEarningTypeSelect,
}: UseReportSearchParams) {
  const { featureTimezoneName } = useAppSelector(selectFeatureTimezone)
  const { maxDate, rangePresets1 } = useRangePickerOptions()

  const [searchValues, setSearchValues] = useState<ReportSearchValues>({
    period: ReportPeriod.Day,
    earningType: EarningType.GrossEarnings,
    startAt: monthAgo.tz(featureTimezoneName, true).startOf('day').toDate(),
    endAt: yesterday.tz(featureTimezoneName, true).endOf('day').toDate(),
  })
  const [dayDiff, setDayDiff] = useState(defaultDiff)
  const { period } = searchValues

  const [creatorsFilterModal, openCreatorsFilterModal] = useFormModal<{
    creatorIds: number[]
  }>({
    onSubmit: ({ creatorIds }) => {
      setSearchValues({ ...searchValues, creatorIds })
    },
    getFields: () => (
      <CreatorsFilterFields creatorIds={searchValues.creatorIds || []} />
    ),
    title: 'Filter options',
    submitText: 'Apply',
    modalProps: { centered: true },
  })

  const [employeesFilterModal, openEmployeesFilterModal] = useFormModal<{
    employeeIds: number[]
    groupIds: number[]
  }>({
    onSubmit: ({ employeeIds, groupIds }) => {
      setSearchValues({
        ...searchValues,
        employeeIds,
        groupIds,
      })
    },
    getFields: () => <EmployeesFilterFields />,
    title: 'Filter options',
    submitText: 'Apply',
    modalProps: { centered: true },
  })

  return {
    searchValues,
    searchElements: (
      <Flex>
        <Space>
          <RangePicker
            size="small"
            presets={rangePresets1}
            maxDate={maxDate}
            popupClassName={s.presetDropdown}
            defaultValue={[monthAgo, yesterday]}
            allowClear={false}
            allowEmpty={false}
            onChange={(values: [Dayjs|null, Dayjs|null]|null) => {
              const start = values?.[0]
              const end = values?.[1]
              if (!start || !end) return

              const diff = end.diff(start, 'd')
              setDayDiff(diff)

              setSearchValues({
                ...searchValues,
                period: autoCorrectPeriod(period, diff),
                startAt: dayjs(start)
                  .tz(featureTimezoneName, true)
                  .startOf('day')
                  .toDate(),
                endAt: dayjs(end)
                  .tz(featureTimezoneName, true)
                  .endOf('day')
                  .toDate(),
              })
            }}
          />
          <Select
            size="small"
            options={periodOptions.map(({ label, value }) => {
              return {
                label,
                value,
                disabled: autoCorrectPeriod(value, dayDiff) !== value,
              }
            })}
            value={searchValues.period}
            onChange={(value) =>
              setSearchValues({ ...searchValues, period: value })
            }
          />
          <Tooltip title={periodTooltipTitle[period]}>
            <Text type="secondary">
              <InfoCircleOutlined />
            </Text>
          </Tooltip>
          {enableEarningTypeSelect && (
            <Select
              size="small"
              options={earningTypeOptions}
              value={searchValues.earningType}
              onChange={(value) =>
                setSearchValues({ ...searchValues, earningType: value })
              }
            />
          )}
          {enableCreatorsFilter && (
            <Button
              size="small"
              type="primary"
              icon={<FilterOutlined />}
              ghost
              onClick={() =>
                openCreatorsFilterModal({
                  creatorIds: searchValues.creatorIds,
                })
              }
            >
              Filters
            </Button>
          )}
          {enableEmployeesFilter && (
            <Button
              size="small"
              type="primary"
              icon={<FilterOutlined />}
              ghost
              onClick={() =>
                openEmployeesFilterModal({
                  employeeIds: searchValues.employeeIds,
                  groupIds: searchValues.groupIds,
                })
              }
            >
              Filters
            </Button>
          )}
        </Space>
        {enableCreatorsFilter && creatorsFilterModal}
        {enableEmployeesFilter && employeesFilterModal}
      </Flex>
    ),
  }
}
